import React from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  TextField,
  Grid,
  Typography,
  Card,
  CardContent,
  Skeleton,
} from '@mui/material';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';
import makeStyles from '@mui/styles/makeStyles';

import TagsAutocomplete from '../../../../../../../components/Autocompletes/Tags';

import styles from './styles';

const useStyles = makeStyles(styles);

export const GeneralFormComponent = (props) => {
  const classes = useStyles();
  const {
    eventTemplate,
    barn,
    handleNameChange,
    handleChangeTags,
    handleDescriptionChange,
    isLoading,
    tags,
    allTags,
  } = props;

  // Find the horse or barn task id in event template tags, set value if present
  const taskTypeTag = (tags || [])
    .find((t) => t.id === barn.barnMetadata.horseTaskTypeTagId || t.id === barn.barnMetadata.barnTaskTypeTagId) || { id: null };

  const handleChangeTaskType = (e) => {
    handleChangeTags(
      [
        ...tags.filter((t) => (t.tagType.id === barn.barnMetadata.tagsTagTypeId || t.tagType.id === barn.barnMetadata.healthTagTypeId)),
        allTags.find((t) => t.id.toString() === e.target.value)],
    );
  };

  const label = (
    <Grid item xs={12} md={3} lg={2}>
      <Typography variant="subtitle2">General Task Details</Typography>
      <Typography variant="caption" gutterBottom>
        Define a task by giving it a name, description, and task.
      </Typography>
    </Grid>
  );

  if (isLoading) {
    return (
      <Grid container spacing={4}>
        {label}
        <Grid item xs={12} lg={10} container className={classes.section}>
          <Card className={classes.card}>
            <CardContent>
              <Skeleton className={classes.nameField} />
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={4}>
      {label}
      <Grid item xs={12} lg={10} container className={classes.section}>
        <Card className={classes.card}>
          <CardContent>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <TextField
                  error={!eventTemplate.validation.name.isValid}
                  margin="normal"
                  fullWidth
                  required
                  variant="outlined"
                  id="name"
                  label="Name"
                  value={eventTemplate.name}
                  autoFocus
                  onChange={handleNameChange}
                  className={classes.nameField}
                  helperText={
                    !eventTemplate.validation.name.isValid
                      && eventTemplate.validation.name.message
                  }
                />
                {
                  barn.barnMetadata && (
                    <FormControl
                      margin="normal"
                      fullWidth
                      variant="outlined"
                    >
                      <FormLabel id="task-type-radio-buttons-group-label">Task Type</FormLabel>
                      <RadioGroup
                        row
                        aria-labelledby="task-type-radio-buttons-group-label"
                        name="task-type-radio-buttons-group"
                        onChange={handleChangeTaskType}
                        value={taskTypeTag.id}
                      >
                        <FormControlLabel value={barn.barnMetadata.horseTaskTypeTagId} control={<Radio />} label="Horse" />
                        <FormControlLabel value={barn.barnMetadata.barnTaskTypeTagId} control={<Radio />} label="Barn/Location (chores)" />
                        {/* // Leaving out for MVP to reduce confusion */}
                        {/* <FormControlLabel value={barn.barnMetadata.administrativeTaskTypeTagId} control={<Radio />} label="Admin (other)" /> */}
                      </RadioGroup>

                      <FormHelperText>
                        {
                          (taskTypeTag && taskTypeTag.id === barn.barnMetadata.horseTaskTypeTagId) && (
                            'Horse tasks are available when creating or scheduling a task for a horse (Shoeing/farrier, Vaccine, Grooming, etc.)'
                          )
                        }
                        {
                          (taskTypeTag && taskTypeTag.id === barn.barnMetadata.barnTaskTypeTagId) && (
                            'Barn tasks are available when creating or scheduling a task for a location (Chores, Feed Prep, Drag Ring etc.)'
                          )
                        }
                      </FormHelperText>
                    </FormControl>
                  )
                }
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  error={!eventTemplate.validation.description.isValid}
                  margin="normal"
                  fullWidth
                  variant="outlined"
                  id="description"
                  label="Description"
                  value={eventTemplate.description}
                  onChange={handleDescriptionChange}
                  multiline
                  rows={3}
                  helperText={
                    !eventTemplate.validation.description.isValid
                    && eventTemplate.validation.description.message
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TagsAutocomplete
                  onSelect={(e, value) => { handleChangeTags(value); }}
                  value={tags}
                  size="default"
                />
                <FormHelperText sx={{ marginLeft: '14px' }}>
                  Use tags to group together similar tasks or to denote distinctions and/or categories.
                </FormHelperText>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

GeneralFormComponent.propTypes = {
  eventTemplate: PropTypes.object,
  barn: PropTypes.object,
  tags: PropTypes.array,
  allTags: PropTypes.array,
  handleNameChange: PropTypes.func,
  handleChangeTags: PropTypes.func,
  handleDescriptionChange: PropTypes.func,
  isLoading: PropTypes.bool,
};

GeneralFormComponent.defaultProps = {
  eventTemplate: {},
  barn: { barnMetadata: {} },
  tags: [],
  allTags: [],
  handleNameChange: () => {},
  handleChangeTags: () => {},
  handleDescriptionChange: () => {},
  isLoading: true,
};

export default GeneralFormComponent;
